import { COLORS } from './colors';
import { getContrastTextColor } from './commonFunctions';

export const SIZING = {
  // Page and layout spacing
  pagePadding: '1.25rem',   // Padding for main content area
  sectionGap: '1.5rem',     // Spacing between main sections
  cardGap: '1rem',          // Spacing inside cards/smaller groups
  containerPadding: '1rem', // Internal card/container padding
  
  // Component heights
  buttonHeight: '2.5rem',   // Standard button height (approx 40px)
  compactButtonHeight: '2rem', // Small/compact button height (approx 32px)
  tableRowHeight: '3.25rem', // Standard table row height (approx 52px)
  compactTableRowHeight: '2.5rem', // Compact table row height (approx 40px)
  headerHeight: '4rem',     // Global header height (approx 64px)
  
  // Widths
  sidebarWidth: '17.5rem',  // Main sidebar width (expanded)
  sidebarCollapsed: '5rem', // Main sidebar width (collapsed)
  kanbanColumnWidth: '20rem', // 320px
  scrollbarWidth: '0.375rem', // 6px
  
  // Table Sizing
  table: {
    padding: '0.75rem 1rem',
    headerHeight: '3rem',
    rowHeight: '3.25rem'
  },

  // Spacing System (Margin, Padding, Gaps)
  margin: {
    xxs: '0.25rem',
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem'
  },

  padding: {
    xxs: '0.25rem',
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem'
  },

  gap: {
    xxs: '0.125rem',
    xs: '0.25rem',
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.5rem'
  },

  // Radius
  radius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px'
  },
  
  // Modal Sizes
  modal: {
    sm: '25rem',  // Small modals (400px)
    md: '37.5rem', // Medium modals (600px)
    lg: '50rem',   // Large modals (800px)
    xl: '62.5rem'  // Extra large modals (1000px)
  },

  // Font Sizes (Standard scale)
  font: {
    xxs: '0.625rem', // 10px
    xs: '0.75rem',  // 12px
    sm: '0.875rem', // 14px
    md: '1rem',     // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem',  // 20px
    h5: '1.5rem',   // 24px (PMPI header standard)
    h4: '1.75rem'   // 28px
  }
};

export const COMMON_SX = {
  pageContainer: {
    p: SIZING.pagePadding,
    display: 'flex',
    flexDirection: 'column',
    gap: SIZING.sectionGap,
    height: '100%',
    overflow: 'auto',
    bgcolor: COLORS.background
  },
  
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: SIZING.margin.md
  },
  
  card: {
    bgcolor: COLORS.white,
    borderRadius: SIZING.radius.lg,
    boxShadow: COLORS.ui.shadowLight,
    p: SIZING.containerPadding
  },

  actionButton: {
    height: SIZING.buttonHeight,
    borderRadius: SIZING.radius.md,
    textTransform: 'none',
    fontWeight: 600,
    px: SIZING.padding.md
  }
};
export const labelSelectorStyles = {
  labelTitle: {
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },

  formControl: {
    mb: 3
  },

  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5
  },

  chip: (bgcolor) => ({
    color: getContrastTextColor(bgcolor),
    fontWeight: 500,
    bgcolor
  }),

  menuProps: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: {
      style: {
        maxHeight: 200,
        maxWidth: 300,
        overflowY: 'auto'
      },
      onMouseDown: (e) => {
        if (e.target.closest('.create-option')) {
          e.preventDefault();
        }
      }
    }
  },

  editingContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    width: '100%'
  },

  editingTextField: {
    flex: 1,
    '& .MuiTextField-root': {
      marginBottom: 0
    },
    '& .MuiFormHelperText-root': {
      position: 'static',
      marginTop: '4px',
      lineHeight: 1.2
    }
  },

  inputControlsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    marginTop: '8px'
  },

  colorInput: {
    width: 24,
    height: 24,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    outline: 'none',
    backgroundColor: 'transparent'
  },

  iconButton: {
    fontSize: 16
  },

  successButton: {
    color: COLORS.success
  },

  errorButton: {
    color: COLORS.error
  },

  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },

  labelInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    minWidth: 0,
    flex: 1
  },

  colorDot: {
    width: 16,
    height: 16,
    borderRadius: '50%'
  },

  actionButtons: {
    display: 'flex',
    gap: 0.5
  },

  actionIconButton: {
    p: 0.25
  },

  createOption: {
    borderTop: `1px solid ${COLORS.gray[200]}`
  },

  createContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: COLORS.primary
  },

  createFormContainer: {
    borderTop: `1px solid ${COLORS.gray[200]}`,
    '&:hover': {
      backgroundColor: 'transparent !important'
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent !important'
    }
  }
};

export const checklistStyles = {
  header: {
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  progressContainer: {
    mb: 2
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    mb: 1
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    bgcolor: COLORS.gray[200]
  },
  list: {
    mb: 2
  },
  listItemReadOnly: {
    px: 0,
    py: 0.25,
    alignItems: 'flex-start'
  },
  listItemEditable: {
    px: 0,
    py: 0.25,
    pr: 6,
    alignItems: 'flex-start'
  },
  listItemIcon: {
    minWidth: 36,
    mt: 0.25
  },
  textFieldCompleted: {
    '& .MuiInput-input': {
      color: COLORS.gray[700],
      fontWeight: 700,
      fontSize: '1rem',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInput-underline:after': {
      display: 'none'
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      display: 'none'
    }
  },
  textFieldIncomplete: {
    '& .MuiInput-input': {
      textDecoration: 'none',
      color: 'text.primary',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInput-underline:after': {
      display: 'none'
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      display: 'none'
    }
  },
  secondaryAction: {
    right: 0,
    top: 18
  },
  deleteButton: {
    color: COLORS.error
  },
  deleteIcon: {
    fontSize: 16
  },
  addContainer: {
    display: 'flex',
    gap: 1,
    alignItems: 'center'
  },
  addTextField: {
    flex: 1
  },
  addButton: {
    color: COLORS.primary
  },
  selectMenuProps: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: {
      sx: {
        maxHeight: 200,
        overflowY: 'auto'
      }
    },
    MenuListProps: {
      sx: {
        maxHeight: 200,
        overflowY: 'auto'
      }
    }
  },
  container: { p: 0 },
  headerPaper: {
    p: 0,
    mb: 2,
    border: `1px solid ${COLORS.ui.border}`,
    borderRadius: 2,
    bgcolor: COLORS.white,
    overflow: 'hidden'
  },
  headerRow: { 
    display: 'flex', 
    alignItems: 'center', 
    gap: 1, 
    p: 1.5,
    bgcolor: COLORS.timesheet.boxBorder, // Light blue background
    borderBottom: `1px solid ${COLORS.ui.border}`
  },
  headerIcon: { fontSize: 20, color: COLORS.gray[700] },
  countChip: {
    ml: 'auto',
    height: 22,
    fontSize: '0.72rem',
    fontWeight: 600,
    bgcolor: COLORS.gray[900],
    color: COLORS.white
  },
  plusIconButton: {
    color: COLORS.gray[700],
    bgcolor: COLORS.gray[100],
    '&:hover': { bgcolor: COLORS.gray[200] }
  },
  progressRow: { display: 'flex', justifyContent: 'space-between', mb: 1 },
  progressLeftText: { color: COLORS.gray[600] },
  progressRightText: { fontWeight: 600, color: COLORS.gray[700] },
  progressLinear: {
    height: 6,
    borderRadius: 999,
    bgcolor: COLORS.gray[200]
  },
  groupsWrapper: { mb: 2 },
  groupPaper: {
    mb: 2,
    borderRadius: 2,
    overflow: 'hidden',
    border: `1px solid ${COLORS.ui.border}`,
    bgcolor: COLORS.white
  },
  groupHeader: {
    p: 1.5,
    background: COLORS.white,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '&:hover': { background: COLORS.gray[50] }
  },
  groupExpandIcon: (isExpanded) => ({
    transition: 'transform 0.2s ease',
    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
    color: COLORS.gray[600],
    fontSize: 20
  }),
  groupAssignmentIcon: { color: COLORS.indigo[600], fontSize: 18 },
  groupTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: COLORS.gray[800]
  },
  groupCountChip: {
    bgcolor: COLORS.white,
    color: COLORS.gray[700],
    fontWeight: 600,
    fontSize: '0.72rem',
    height: 18,
    border: `1px solid ${COLORS.gray[200]}`
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: 1.25,
    borderBottom: `1px solid ${COLORS.ui.border}`,
    bgcolor: COLORS.gray[50],
    color: COLORS.gray[500],
    '& .MuiTypography-root': {
      fontSize: '0.8rem',
      fontWeight: 800,
      color: COLORS.gray[500]
    }
  },
  colChecklist: { flex: 1, minWidth: 0, px: 1 },
  colAssignee: { width: 65, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' },
  colPriority: { width: 60, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' },
  colDueDate: { width: 85, textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  colActions: { textAlign: 'right' },
  groupProgressLinear: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    borderRadius: 0,
    bgcolor: COLORS.gray[200],
    '& .MuiLinearProgress-bar': {
      borderRadius: 0,
      bgcolor: COLORS.primary
    }
  },
  groupBody: (isDropTarget) => ({
    p: 2,
    minHeight: 56,
    border: isDropTarget ? `2px dashed ${COLORS.primary}` : '2px dashed transparent',
    borderRadius: 2,
    transition: 'border-color 0.2s ease'
  }),
  inlineAddPaper: {
    p: 1.5,
    mt: 1.5,
    mb: 1,
    borderRadius: 2,
    border: `1px solid ${COLORS.ui.border}`,
    bgcolor: COLORS.gray[50]
  },
  flexRowWrapBottom: { display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' },
  outlinedInputCommon: {
    '& .MuiOutlinedInput-root': {
      bgcolor: COLORS.white,
      '& fieldset': { borderColor: COLORS.gray[300] },
      '&:hover fieldset': { borderColor: COLORS.primary },
      '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 2 }
    }
  },
  inlineTextField: {
    flex: 1,
    minWidth: 200
  },
  dateField: {
    minWidth: 160,
    '& .MuiInputBase-input': { px: 1.25 }
  },
  floatingSelectControl: (minWidth = 140) => ({
    minWidth,
    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
    '& .MuiInputLabel-shrink': {
      px: 0.5,
      bgcolor: COLORS.white,
      zIndex: 1,
      transform: 'translate(14px, -7px) scale(0.75)'
    }
  }),
  selectControl: {
    bgcolor: COLORS.white,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.gray[300] },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.primary, borderWidth: 2 }
  },
  inlineAddButton: {
    minWidth: 100,
    height: 36,
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 500,
    bgcolor: COLORS.primary
  },
  footerAddPaper: {
    p: 2,
    mt: 2,
    borderRadius: 2,
    border: `1px solid ${COLORS.ui.border}`,
    bgcolor: COLORS.white
  },
  footerTitleFieldWrap: { flex: '0 0 auto', minWidth: 200 },
  footerTitleLabel: { display: 'block', mb: 0.5, fontWeight: 600, color: COLORS.gray[600] },
  footerAddButton: {
    minWidth: 120,
    height: 36,
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 500,
    bgcolor: COLORS.primary,
    '&:disabled': { background: COLORS.gray[300] }
  },
  cancelButton: { height: 36, textTransform: 'none' },
  itemCard: (isDragging, isDragTarget, dragPosition, readOnly, isEditing) => ({
    mb: 0,
    transition: 'all 0.15s ease',
    border: 'none !important',
    borderBottom: `1px solid ${COLORS.ui.border} !important`,
    '&:last-child': { borderBottom: 'none !important' },
    backgroundColor: COLORS.white,
    borderRadius: 0,
    opacity: isDragging ? 0.5 : 1,
    cursor: !readOnly && !isEditing ? 'grab' : 'default',
    boxShadow: isDragTarget ? `0 0 0 2px ${COLORS.primary}55` : undefined,
    borderTop: isDragTarget && dragPosition === 'before' ? `3px solid ${COLORS.primary}` : undefined,
    // borderBottom: isDragTarget && dragPosition === 'after' ? `3px solid ${COLORS.primary}` : undefined,
    '&:hover': { bgcolor: COLORS.gray[50] }
  }),
  itemCardContent: { p: 0, '&:last-child': { pb: 0 } },
  itemRow: { display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, gap: 1 },
  checkboxWrap: { pt: 0.5 },
  checkboxButton: (completed) => ({
    p: 0.5,
    transition: 'all 0.2s ease',
    '&:hover .controls': {
      transform: 'scale(1.1)',
      bgcolor: completed ? COLORS.success + '20' : COLORS.primary + '20',
      display: 'none !important'
    }
  }),
  checkIcon: { fontSize: 20, color: COLORS.success },
  uncheckIcon: { fontSize: 20, color: COLORS.gray[500] },
  itemContent: { flex: 1, minWidth: 0 },
  editContainer: { display: 'flex', flexDirection: 'column', gap: 1.5 },
  editRow: { display: 'flex', gap: 1, flexWrap: 'wrap' },
  editTextField: {
    '& .MuiOutlinedInput-root': {
      bgcolor: COLORS.white,
      '& fieldset': { borderColor: COLORS.primary },
      '&:hover fieldset': { borderColor: COLORS.primary },
      '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 2 }
    }
  },
  editDateField: {
    minWidth: 160,
    '& .MuiOutlinedInput-root': {
      bgcolor: COLORS.white,
      '& fieldset': { borderColor: COLORS.gray[300] },
      '&:hover fieldset': { borderColor: COLORS.primary },
      '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 2 }
    },
    '& .MuiInputBase-input': { px: 1.25 }
  },
  editActionsRow: { display: 'flex', gap: 1 },
  saveButton: { bgcolor: COLORS.success, color: COLORS.white, '&:hover': { bgcolor: COLORS.success + 'dd' } },
  itemTitle: (completed, readOnly) => ({
    textDecoration: 'none',
    opacity: 1,
    color: completed ? COLORS.gray[700] : COLORS.gray[900],
    cursor: readOnly ? 'default' : 'pointer',
    fontWeight: 700,
    lineHeight: 1.45,
    fontSize: '1rem',
    transition: 'color 0.2s ease',
    '&:hover': !readOnly ? { color: COLORS.primary } : {}
  }),
  metadataRow: { display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' },
  priorityToken: (isCompleted, color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.35,
    px: 0.55,
    py: 0.2,
    borderRadius: 1,
    bgcolor: 'transparent',
    color: isCompleted ? COLORS.gray[500] : color,
    border: `1px solid ${isCompleted ? COLORS.gray[300] : `${color}2e`}`,
    fontSize: '0.68rem',
    fontWeight: 600,
    lineHeight: 1
  }),
  metaFlagIcon: { fontSize: 11 },
  dueToken: (isCompleted, status) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.35,
    px: 0.55,
    py: 0.2,
    borderRadius: 1,
    color: isCompleted
      ? COLORS.gray[500]
      : status === 'overdue'
        ? COLORS.red.dark
        : status === 'today' || status === 'soon'
          ? COLORS.orange[600]
          : COLORS.gray[600],
    border: `1px solid ${
      isCompleted
        ? COLORS.gray[300]
        : status === 'overdue'
          ? COLORS.red.light
          : status === 'today' || status === 'soon'
            ? COLORS.orange[100]
            : COLORS.gray[300]
    }`,
    fontSize: '0.68rem',
    fontWeight: 600,
    lineHeight: 1
  }),
  metaScheduleIcon: { fontSize: 11 },
  assigneeToken: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.35,
    color: COLORS.gray[700],
    fontSize: '0.7rem',
    fontWeight: 600,
    maxWidth: 160
  },
  assigneeAvatar: {
    width: 18,
    height: 18,
    fontSize: '0.62rem',
    bgcolor: COLORS.gray[700],
    color: COLORS.white
  },
  itemActions: (hovered) => ({
    display: 'flex',
    gap: 0.5,
    opacity: hovered ? 1 : 0,
    transition: 'opacity 0.2s ease'
  }),
  editIconButton: {
    color: COLORS.gray[500],
    '&:hover': { color: COLORS.primary, bgcolor: COLORS.primary + '15' }
  },
  deleteIconButton: {
    color: COLORS.gray[500],
    '&:hover': { color: COLORS.error, bgcolor: COLORS.error + '15' }
  },
  actionIcon: { fontSize: 16 }
};

export const timesheetStyles = {
  tableContainer: {
    boxShadow: 2,
    overflow: 'auto',
    '& .MuiTable-root': {
      minWidth: '100%'
    }
  },

  mainTable: {
    tableLayout: 'fixed'
  },

  userRow: {
    cursor: 'pointer'
  },

  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },

  avatar: {
    width: 32,
    height: 32
  },

  expandedCell: {
    py: 0,
    border: 0
  },

  expandedBox: {
    py: 2,
    bgcolor: 'grey.25'
  },

  nestedTable: {
    tableLayout: 'fixed',
    minWidth: '100%',
    '& .MuiTableCell-root': {
      wordBreak: 'break-word',
      overflow: 'hidden'
    }
  },

  ticketInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },

  ticketLink: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },

  ticketTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    maxWidth: '300px'
  },

  noDataCell: {
    py: 4
  },

  summaryTab: {
    bgcolor: COLORS.white,
    borderRadius: '1px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(8, 43, 114, 0.12)'
  },

  summaryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 2,
    cursor: 'pointer',
    bgcolor: 'primary.main',
    color: COLORS.white,
    '&:hover': {
      bgcolor: 'primary.dark'
    }
  },

  summaryTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  }
};

export const dividerLine = {
  flex: 1,
  height: '1px',
  bgcolor: COLORS.gray[300]
};

export const getLabelChipStyles = (color) => ({
  width: 14,
  height: 14,
  borderRadius: '50%',
  bgcolor: color || COLORS.primary,
  cursor: 'pointer',
  border: '1px solid rgba(255,255,255,0.8)',
  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'scale(1.2)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
  },
  transition: 'all 0.2s ease-in-out'
});

export const overdueBoxStyles = {
  mb: 1.5,
  p: 1,
  bgcolor: COLORS.red[100],
  borderRadius: 1,
  border: `1px solid ${COLORS.red[200]}`,
  display: 'block'
};

export const overdueTextStyles = {
  fontSize: '10px',
  color: COLORS.red[700],
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 0.25
};

export const tableHeaderStyles = {
  row: {
    bgcolor: COLORS.gray[50]
  },
  cell: {
    fontWeight: 600,
    color: COLORS.text.secondary,
    fontSize: '14px',
    '@media (max-width: 1380px)': {
      fontSize: '13px',
      py: 1,
      px: 1.5,
      whiteSpace: 'nowrap'
    }
  }
};

export const taskListStyles = {
  container: {
    maxHeight: 'calc(100vh - 280px)',
    overflowY: 'auto',
    pb: 8
  },
  accordion: {
    mb: 1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: `1px solid ${COLORS.gray[200]}`
  },
  accordionSummary: {
    bgcolor: COLORS.gray[100],
    color: COLORS.text.primary,
    '@media (max-width: 1380px)': {
      minHeight: '40px !important',
      py: 0.5,
      '& .MuiAccordionSummary-content': {
        my: '4px !important'
      }
    }
  },
  accordionDetails: {
    p: 0
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    '@media (max-width: 1380px)': {
      gap: 1.5
    }
  },
  statusTitle: {
    fontWeight: 600,
    fontSize: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    '@media (max-width: 1380px)': {
      fontSize: '14px'
    }
  },
  statusChip: {
    bgcolor: COLORS.gray[200],
    color: COLORS.text.secondary,
    fontWeight: 600,
    flexShrink: 0,
    '@media (max-width: 1380px)': {
      fontSize: '11px',
      height: '20px'
    }
  },
  scrollContainer: {
    maxHeight: '380px',
    overflowY: 'auto',
    pb: 4,
    '@media (max-width: 1380px)': {
      maxHeight: 'calc(100vh - 290px)',
      pb: 2
    }
  },
  taskRow: (isOverdue, loadingTaskId, taskId) => ({
    '&:hover': {
      bgcolor: loadingTaskId === taskId ? COLORS.blue[50] : (isOverdue ? COLORS.red[100] : COLORS.gray[50]),
      cursor: loadingTaskId === taskId ? 'wait' : 'pointer'
    },
    bgcolor: loadingTaskId === taskId ? COLORS.blue[25] : (isOverdue ? COLORS.red[50] : 'transparent'),
    pointerEvents: loadingTaskId === taskId ? 'none' : 'auto',
    borderLeft: isOverdue ? `4px solid ${COLORS.red[400]}` : 'none'
  }),
  taskCell: {
    '@media (max-width: 1380px)': {
      py: '8px !important',
      px: '12px !important',
      fontSize: '13px'
    }
  },
  taskTitle: {
    fontSize: '14px',
    color: COLORS.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px',
    '@media (max-width: 1380px)': {
      fontSize: '13px',
      maxWidth: '220px'
    }
  },
  codeChip: {
    bgcolor: COLORS.gray[50],
    color: COLORS.gray[500],
    fontSize: '11px',
    fontWeight: 600
  },
  subtaskCount: {
    fontSize: '11px',
    color: COLORS.blue[600],
    fontWeight: 600
  },
  priorityText: {
    fontSize: '13px',
    color: COLORS.gray[500],
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px'
  },
  avatar: {
    width: 28,
    height: 28,
    fontSize: '12px',
    bgcolor: COLORS.primary,
    fontWeight: 600
  },
  assigneeText: {
    fontSize: '13px',
    color: COLORS.gray[500],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px'
  },
  dueDateText: {
    fontSize: '13px',
    color: COLORS.gray[500],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  iconSmall: {
    fontSize: 14
  },
  iconMedium: {
    fontSize: 16
  },
  expandIcon: {
    fontSize: 16
  },
  noTasksCell: {
    textAlign: 'center',
    py: 4
  },
  loadingCell: {
    textAlign: 'center',
    py: 2
  },
  spacerRow: {
    height: '60px',
    '@media (max-width: 1380px)': {
      height: '8px'
    }
  },
  spacerCell: {
    border: 'none',
    p: 0
  },
  expandedCell: {
    py: 0
  }
};

export const taskModalButtonStyles = {
  outlinedWhite: {
    textTransform: 'none',
    borderColor: 'rgba(255,255,255,0.4)',
    color: COLORS.white,
    '&:hover': {
      borderColor: 'rgba(255,255,255,0.7)',
      bgcolor: 'rgba(255,255,255,0.08)'
    }
  }
};

export const taskTitleFieldStyles = {
  flex: 1,
  '& .MuiInput-input': {
    fontSize: '1rem',
    fontWeight: 400,
    padding: '4px 0'
  },
  '& .MuiInput-underline:before': {
    display: 'none'
  },
  '& .MuiInput-underline:after': {
    display: 'none'
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    display: 'none'
  }
};

export const taskSelectStyles = {
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '& .MuiSelect-select': { padding: '4px 0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' }
};

export const taskSelectMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      maxWidth: 300,
      overflow: 'auto'
    },
    onMouseDown: (e) => {
      // Prevents the menu from closing when clicking inside a "Create New" text field
      if (e.target.closest('.create-option')) {
        e.preventDefault();
      }
    }
  },
  MenuListProps: { autoFocusItem: false }
};

export const taskDateFieldStyles = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' }
  },
  '& .MuiInputBase-input': { padding: '4px 0', fontSize: '0.875rem' }
};

export const taskAssigneeAvatarStyles = {
  width: 20,
  height: 20,
  fontSize: '10px',
  bgcolor: COLORS.green[500]
};

export const taskStatusChipStyles = {
  color: COLORS.white,
  fontWeight: 600
};

export const smallIconButtonStyles = {
  width: 24,
  height: 24,
  bgcolor: COLORS.slate[50],
  '&:hover': { bgcolor: COLORS.slate[100] }
};

export const primaryActionButtonStyles = {
  width: 24,
  height: 24,
  bgcolor: COLORS.primary,
  color: COLORS.white,
  '&:hover': { bgcolor: COLORS.blue[600] },
  '&:disabled': { bgcolor: COLORS.gray[200], color: COLORS.gray[500] }
};

export const dangerActionButtonStyles = {
  width: 24,
  height: 24,
  bgcolor: COLORS.red[500],
  color: COLORS.white,
  '&:hover': { bgcolor: COLORS.red[600] },
  '&:disabled': { bgcolor: COLORS.gray[200], color: COLORS.gray[500] }
};

export const hoverEditIconButtonStyles = {
  p: 0.25,
  '&:hover': {
    bgcolor: COLORS.blue[50],
    '& .edit-icon': {
      color: COLORS.blue[600]
    }
  }
};

export const hoverDeleteIconButtonStyles = {
  p: 0.25,
  '&:hover': {
    bgcolor: COLORS.red[50],
    '& .delete-icon': {
      color: COLORS.red[600]
    }
  }
};

export const expandableChipStyles = {
  cursor: 'pointer',
  color: COLORS.primary,
  borderColor: COLORS.primary,
  fontSize: '12px',
  fontWeight: 500,
  '&:hover': {
    bgcolor: COLORS.blue[50],
    borderColor: COLORS.blue[600],
    color: COLORS.blue[600]
  }
};

export const bulkEditModalStyles = {
  dialogPaper: {
    borderRadius: 3,
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  dialogTitle: {
    bgcolor: COLORS.primary,
    color: COLORS.white,
    py: 3,
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  editIcon: {
    fontSize: 28
  },
  titleText: {
    mb: 0.5
  },
  subtitleText: {
    opacity: 0.9
  },
  dialogContent: {
    p: 0
  },
  previewPaper: {
    m: 3,
    p: 2,
    bgcolor: COLORS.blue[50],
    border: `1px solid ${COLORS.blue[200]}`
  },
  previewTitle: {
    mb: 1.5,
    color: COLORS.blue[800],
    fontWeight: 600
  },
  tasksContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    maxHeight: 120,
    overflow: 'auto',
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': { bgcolor: COLORS.gray[100] },
    '&::-webkit-scrollbar-thumb': { bgcolor: COLORS.gray[400], borderRadius: '3px' }
  },
  taskChip: {
    fontSize: '12px',
    bgcolor: COLORS.white,
    border: `1px solid ${COLORS.blue[300]}`,
    '&:hover': { bgcolor: COLORS.blue[100] }
  },
  formContainer: {
    px: 3,
    pb: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: 3
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1
  },
  fieldIcon: {
    fontSize: 20,
    color: COLORS.primary
  },
  fieldLabel: {
    fontWeight: 600,
    color: COLORS.gray[800]
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.primary
      }
    }
  },
  select: {
    borderRadius: 2,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.primary
    }
  },
  menuProps: {
    PaperProps: {
      style: {
        maxHeight: 200,
        overflow: 'auto'
      }
    }
  },
  noChangeMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontStyle: 'italic',
    color: COLORS.gray[500]
  },
  menuItemBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5
  },
  menuAvatar: {
    width: 28,
    height: 28,
    fontSize: '12px',
    bgcolor: COLORS.primary
  },
  statusAvatar: {
    width: 28,
    height: 28,
    fontSize: '12px',
    bgcolor: COLORS.primary
  },
  statusIcon: {
    fontSize: 14
  },
  dialogActions: {
    px: 3,
    py: 2,
    bgcolor: COLORS.gray[50],
    borderTop: `1px solid ${COLORS.gray[200]}`,
    gap: 1
  },
  cancelButton: {
    borderColor: COLORS.gray[300],
    color: COLORS.gray[600],
    '&:hover': {
      borderColor: COLORS.gray[400],
      bgcolor: COLORS.gray[50]
    }
  },
  updateButton: {
    bgcolor: COLORS.primary,
    '&:hover': { bgcolor: COLORS.blue[600] },
    '&:disabled': {
      bgcolor: COLORS.gray[200],
      color: COLORS.gray[500]
    },
    px: 3,
    py: 1
  }
};

export const bulkDeleteModalStyles = {
  dialogPaper: {
    borderRadius: 3,
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  dialogTitle: {
    bgcolor: COLORS.red[500],
    color: COLORS.white,
    py: 3,
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  deleteIcon: {
    fontSize: 28
  }
};

export const reportStyles = {
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80vw',
    height: '80vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 3
  },
  ticketsModalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 850,
    maxWidth: '95vw',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 0
  },
  ticketsModalHeader: {
    p: 2,
    borderBottom: `1px solid ${COLORS.slate[200]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  ticketsModalContent: {
    p: 3,
    maxHeight: '60vh',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  calendarDayBox: (dayData, getTicketColor) => ({
    position: 'relative',
    textAlign: 'center',
    cursor: dayData.day ? 'pointer' : 'default',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52, // Tightened height for vertical efficiency
    width: '100%',
    '& .date-accent-bg': {
      width: 48,
      height: 48,
      borderRadius: '6px',
      bgcolor: dayData.day ? COLORS.slate[100] : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 0.25,
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      color: dayData.day ? (dayData.tickets > 0 ? getTicketColor(dayData.tickets) : COLORS.slate[400]) : 'transparent',
      fontSize: '20px',
      fontWeight: dayData.tickets > 0 ? Math.min(800, 500 + (dayData.tickets * 40)) : 500,
    },
    '&:hover .date-accent-bg': {
      bgcolor: dayData.day ? COLORS.slate[200] : 'transparent',
      transform: dayData.day ? 'scale(1.1)' : 'none',
    },
    '&:hover .date-intensity-dot': {
      transform: 'scale(1.5)',
      opacity: 1
    }
  }),
  calendarDayBoxSmall: (dayData, getTicketColor) => ({
    position: 'relative',
    textAlign: 'center',
    cursor: dayData.day ? 'pointer' : 'default',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44, // Balanced height
    '& .date-accent-bg': {
      width: 28,
      height: 28,
      borderRadius: '4px',
      bgcolor: dayData.day ? COLORS.slate[100] : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 0.25,
      transition: 'all 0.2s ease',
      color: dayData.day ? (dayData.tickets > 0 ? getTicketColor(dayData.tickets) : COLORS.slate[400]) : 'transparent',
      fontSize: '12px',
      fontWeight: dayData.tickets > 0 ? Math.min(800, 500 + (dayData.tickets * 40)) : 500,
    },
    '&:hover .date-accent-bg': { 
      bgcolor: COLORS.slate[200],
      transform: 'scale(1.1)'
    }
  }),
  calendarTicketBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    fontSize: '10px',
    fontWeight: 700,
    bgcolor: 'rgba(0,0,0,0.15)',
    borderRadius: '50%',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  calendarTicketBadgeSmall: {
    position: 'absolute',
    top: 2,
    right: 2,
    fontSize: '8px',
    fontWeight: 700,
    bgcolor: 'rgba(0,0,0,0.15)',
    borderRadius: '50%',
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  filterChip: (bgcolor) => ({
    bgcolor: bgcolor,
    color: COLORS.white,
    fontSize: '10px',
    height: 18
  }),
  filterChipExtra: {
    bgcolor: COLORS.slate[500],
    color: COLORS.white,
    fontSize: '10px',
    height: 18
  },
  colorDot: (bgcolor) => ({
    width: 12,
    height: 12,
    borderRadius: '50%',
    bgcolor: bgcolor
  }),
  chartTooltip: {
    backgroundColor: COLORS.white,
    padding: '8px',
    border: `1px solid ${COLORS.disabled.background}`,
    borderRadius: '4px',
    maxWidth: '300px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal'
  },
  chartTooltipText: {
    margin: 0,
    fontSize: '12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal'
  },
  textNoTransform: {
    textTransform: 'none'
  },
  fontWeight600: {
    fontWeight: 600
  },
  selectHeight40: {
    height: 40,
    bgcolor: COLORS.lightBlueBg,
    borderRadius: 1,
    fontSize: '14px'
  },
  textEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  flexGapWrap: {
    display: 'flex',
    gap: 0.5,
    flexWrap: 'wrap'
  },
  tableHeaderCell: {
    fontWeight: 600,
    bgcolor: COLORS.slate[50],
    zIndex: 10
  },
  flexGap3Mb3: {
    display: 'flex',
    gap: 2,
    mb: 2
  },
  cardHeight500: {
    height: 500
  },
  captionText: {
    color: 'text.secondary',
    fontSize: '12.5px'
  },
  avatarSmall: {
    width: 24,
    height: 24,
    fontSize: '11px',
    bgcolor: COLORS.brand.primaryDark
  },
  selectSmall: {
    height: 32,
    fontSize: '13px'
  },
  fontSize13: {
    fontSize: '14px'
  },
  fontSize11: {
    fontSize: '11px'
  },
  fontSize14Weight500: {
    fontSize: '14px',
    fontWeight: 500
  },
  buttonSmall: {
    textTransform: 'none',
    fontSize: '12px'
  },
  buttonContained: {
    bgcolor: COLORS.brand.primaryDark,
    color: COLORS.white,
    '&:hover': {
      bgcolor: COLORS.brand.primaryDarkHover
    },
    textTransform: 'none'
  },
  buttonOutlined: {
    color: COLORS.brand.primaryDark,
    borderColor: COLORS.brand.primaryDark,
    '&:hover': {
      borderColor: COLORS.brand.primaryDarkHover,
      bgcolor: 'rgba(8, 43, 114, 0.04)'
    },
    textTransform: 'none'
  },
  reportSelect: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.slate[200]
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.brand.primaryDark
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.brand.primaryDark
    }
  },
  tableCellWidth40: {
    width: '40%'
  },
  tableCellWidth60: {
    width: '60%'
  },
  noDataContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 420
  },
  tableContainer420: {
    maxHeight: 420
  },
  hoverRow: {
    '&:hover': { bgcolor: COLORS.slate[50] },
    cursor: 'pointer'
  },
  floatingAction: {
    bgcolor: COLORS.white,
    borderRadius: 1,
    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
    '&:hover': { 
      bgcolor: COLORS.slate[50], 
      boxShadow: '0 4px 12px rgba(0,0,0,0.18)' 
    },
    '&:active': { transform: 'scale(0.92)' },
    transition: 'all 0.15s ease',
    p: 0.75 
  }
};

export const reportMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300
    }
  }
};

export const reportMenuPropsWithScroll = {
  PaperProps: {
    style: {
      maxHeight: 300,
      overflow: 'auto'
    }
  }
};

export const reportCommonStyles = {
  cardPadding: {
    p: 1.5,
    '&:last-child': { pb: 1.5 }
  },
  tableCellMaxWidth200: {
    fontSize: '13px',
    maxWidth: 200
  },
  tableCellMaxWidth150: {
    fontSize: '13px',
    maxWidth: 150
  },
  tableCellWidth25: {
    width: '25%',
    maxWidth: 150
  },
  tableCellWidth17: {
    width: '17%'
  },
  tableCellWidth18: {
    width: '18%'
  },
  tableCellWidth50: {
    width: '50%'
  },
  avatarSmallFlexShrink: {
    width: 24,
    height: 24,
    fontSize: '11px',
    bgcolor: COLORS.brand.primaryDark,
    flexShrink: 0
  },
  textEllipsisFullWidth: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%'
  }
};

export const projectManagementStyles = {
  cardElevation: { mb: 4 },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3
  },
  spaceTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  businessIcon: {
    color: COLORS.primary,
    fontSize: 24
  },
  projectChip: {
    fontWeight: 500
  },
  tableHeader: {
    bgcolor: COLORS.background
  },
  projectIcon: {
    width: 40,
    height: 40,
    borderRadius: 2,
    bgcolor: COLORS.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  projectCell: {
    minWidth: 0,
    flex: 1
  },
  projectName: (isAvailableInDropdown) => ({
    cursor: isAvailableInDropdown ? 'pointer' : 'default',
    '&:hover': isAvailableInDropdown ? {
      color: COLORS.primary,
      textDecoration: 'underline'
    } : {},
    color: 'text.primary'
  }),
  projectDescription: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px'
  },
  iconWithText: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  textIcon: {
    color: 'text.secondary',
    fontSize: 18
  },
  actionButtons: {
    display: 'flex',
    gap: 1
  },
  editButton: {
    color: COLORS.success
  },
  viewButton: {
    color: COLORS.warning
  },
  deleteButton: {
    color: COLORS.error
  },
  mainContainer: {
    height: 'calc(100vh - 64px)',
    overflow: 'auto'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 4
  },
  addButton: {
    bgcolor: COLORS.primary
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px'
  },
  emptyStateCard: {
    textAlign: 'center',
    py: 8
  },
  emptyStateIcon: {
    fontSize: 64,
    color: COLORS.gray[400],
    mb: 2
  },
  dialogTitle: {
    pb: 1
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  dialogAvatar: {
    bgcolor: COLORS.primary
  },
  dialogContent: {
    pt: 3
  },
  formField: {
    mb: 3
  },
  menuProps: {
    PaperProps: {
      style: {
        maxHeight: 200,
        width: 'auto'
      }
    }
  },
  menuItem: {
    py: 1.5
  },
  userAvatar: {
    bgcolor: COLORS.primary,
    width: 32,
    height: 32,
    fontSize: '14px'
  },
  teamMembersHeader: {
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  searchField: {
    mb: 2
  },
  membersList: {
    maxHeight: 150,
    overflow: 'auto',
    border: `1px solid ${COLORS.border}`
  },
  selectSpaceMessage: {
    textAlign: 'center',
    color: 'text.secondary'
  },
  dialogActions: {
    p: 3
  },
  createButton: {
    bgcolor: COLORS.primary,
    minWidth: 120
  },
  updateButton: {
    bgcolor: COLORS.success,
    minWidth: 120
  },
  deleteDialogAvatar: {
    bgcolor: COLORS.error
  },
  membersDialogTitle: {
    pb: 2,
    bgcolor: COLORS.background
  },
  membersDialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  membersDialogAvatar: {
    bgcolor: COLORS.warning,
    width: 48,
    height: 48
  },
  membersDialogIcon: {
    fontSize: 24
  },
  membersCount: {
    textAlign: 'center'
  },
  membersCountNumber: {
    fontWeight: 600,
    color: COLORS.primary
  },
  membersDialogContent: {
    pt: 3,
    pb: 4,
    px: 4
  },
  membersListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: COLORS.scrollbar.track
    },
    '&::-webkit-scrollbar-thumb': {
      background: COLORS.scrollbar.thumb,
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: COLORS.scrollbar.thumbHover
    }
  },
  memberCard: (isLead) => ({
    p: 2,
    border: isLead ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.border}`,
    bgcolor: isLead ? COLORS.highlight : COLORS.white
  }),
  memberCardContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  memberInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  memberAvatar: (isLead) => ({
    bgcolor: isLead ? COLORS.primary : COLORS.success,
    width: 44,
    height: 44,
    fontSize: '16px',
    fontWeight: 600
  }),
  memberChips: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  leadChip: {
    fontWeight: 500
  },
  activeChip: {
    fontWeight: 500
  },
  membersDialogActions: {
    p: 3,
    bgcolor: COLORS.background
  }
};

export const addTaskModalStyles = {
  dialogPaper: {
    borderRadius: '12px',
    maxWidth: '800px',
    overflow: 'hidden',
    '@media (max-width: 1380px)': {
      maxWidth: '710px !important',
      maxHeight: 'calc(100vh - 48px) !important',
      m: 1.5
    }
  },
  header: {
    p: 3,
    pb: 2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'relative',
    '&:after': {
      content: '""',
      position: 'absolute',
      left: 24,
      right: 24,
      bottom: 0,
      height: '1px',
      bgcolor: COLORS.gray[200]
    },
    '@media (max-width: 1380px)': {
      pt: 2,
      pb: 1.75,
      px: 2.5,
      '&:after': {
        left: 20,
        right: 20
      }
    }
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: COLORS.slate[800],
    '@media (max-width: 1380px)': {
      fontSize: '1.2rem',
      lineHeight: 1.25
    }
  },
  headerSubtitle: {
    color: COLORS.slate[500],
    fontSize: '1.1rem',
    mt: 0.5,
    '@media (max-width: 1380px)': {
      fontSize: '0.85rem',
      mt: 0.2
    }
  },
  content: {
    px: 4,
    pt: 3,
    pb: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    '@media (max-width: 1380px)': {
      px: 2.5,
      pt: 3.25,
      pb: 2.5,
      gap: 2,
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '6px'
      },
      '&::-webkit-scrollbar-thumb': {
        background: COLORS.gray[300],
        borderRadius: '3px'
      }
    }
  },
  nameField: {
    '@media (max-width: 1380px)': {
      mt: 0.75
    },
    '& .MuiOutlinedInput-root': {
      fontSize: '1.25rem',
      fontWeight: 500,
      '& fieldset': { border: `1px solid ${COLORS.gray[200]}` },
      '&:hover fieldset': { borderColor: COLORS.primary },
      '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 1.5 },
      borderRadius: '8px',
      px: 1,
      '@media (max-width: 1380px)': {
        fontSize: '0.95rem',
        height: '38px',
        px: 0.5
      }
    },
    '& .MuiOutlinedInput-input': {
      '@media (max-width: 1380px)': {
        py: 0,
        height: '100%',
        boxSizing: 'border-box',
        fontSize: '0.95rem'
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '1rem',
      color: COLORS.slate[500],
      transform: 'translate(14px, -10px) scale(0.75)',
      fontWeight: 600,
      bgcolor: COLORS.white,
      px: 0.5,
      '@media (max-width: 1380px)': {
        fontSize: '0.85rem',
        transform: 'translate(14px, -8px) scale(0.75)'
      }
    },
    '& .MuiFormHelperText-root': {
      '@media (max-width: 1380px)': {
        mt: 0.25,
        fontSize: '0.72rem',
        mx: 0.5
      }
    }
  },
  fieldRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1.5,
    rowGap: 2.5,
    alignItems: 'center',
    '@media (max-width: 1380px)': {
      gap: 1.25,
      rowGap: 1.5
    }
  },
  fieldButton: {
    textTransform: 'none',
    border: `1px solid ${COLORS.gray[200]}`,
    borderRadius: '8px',
    px: 2,
    py: 1,
    color: COLORS.gray[600],
    fontSize: '0.95rem',
    gap: 1,
    minWidth: 'auto',
    '&:hover': {
      bgcolor: COLORS.gray[50],
      borderColor: COLORS.gray[300]
    },
    '@media (max-width: 1380px)': {
      px: 1.25,
      py: 0.5,
      fontSize: '0.84rem',
      gap: 0.5,
      height: '34px',
      whiteSpace: 'nowrap',
      '& .MuiTypography-root, & span, & .overflow-tooltip': {
        fontSize: 'inherit !important',
        fontWeight: 'inherit',
        color: 'inherit',
        lineHeight: 'inherit'
      },
      '& .MuiButton-startIcon': {
        mr: 0.6,
        ml: -0.25,
        '& img': {
          width: '15px !important',
          height: '15px !important',
          display: 'block'
        },
        '& .MuiSvgIcon-root': {
          fontSize: '16px !important'
        }
      }
    }
  },
  thickButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    bgcolor: COLORS.ui.border,
    color: COLORS.gray[600],
    py: 1.5,
    px: 2,
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 500,
    '&:hover': {
      bgcolor: COLORS.ui.greyC4C4C4
    },
    '@media (max-width: 1380px)': {
      py: 0.6,
      px: 1.5,
      fontSize: '0.85rem',
      height: '34px'
    }
  },
  richTextActionRow: {
    display: 'flex',
    alignItems: 'center'
  },
  richTextSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    '@media (max-width: 1380px)': {
      gap: 1,
      '& [contenteditable="true"]': {
        minHeight: '80px !important'
      }
    }
  },
  richTextLabel: (variant = 'description') => ({
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    bgcolor: variant === 'acceptance' ? '#FFF4EB' : '#FFFEEC',
    color: COLORS.gray[600],
    borderRadius: '6px',
    px: 2,
    py: 1.25,
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.2,
    '@media (max-width: 1380px)': {
      py: 0.5,
      px: 1.25,
      fontSize: '0.84rem'
    }
  }),
  richTextLabelIcon: {
    fontSize: 18,
    color: COLORS.gray[600],
    '@media (max-width: 1380px)': {
      fontSize: 15
    }
  },
  footer: {
    p: 3,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 24,
      right: 24,
      height: '1px',
      bgcolor: COLORS.gray[200]
    },
    '@media (max-width: 1380px)': {
      py: 2,
      px: 2.5,
      '&:before': {
        left: 20,
        right: 20,
        height: '1px',
        bgcolor: COLORS.gray[200]
      }
    }
  },
  createButton: {
    bgcolor: COLORS.brand.primaryDark,
    color: COLORS.white,
    fontSize: '1.1rem',
    py: 1,
    px: 4,
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': {
      bgcolor: COLORS.brand.primaryDarkHover,
      color: COLORS.white
    },
    '&.Mui-disabled': {
        bgcolor: COLORS.slate[200],
        color: COLORS.slate[400]
    },
    '@media (max-width: 1380px)': {
      fontSize: '0.875rem',
      py: 0.5,
      px: 2.5,
      height: '36px'
    }
  }
};

export const membersDialogStyles = {};

export const estimateTimeStyles = {
  icon: (isRowVariant) => ({
    fontSize: isRowVariant ? '12px' : '10px',
    color: COLORS.gray[400]
  }),
  text: (isRowVariant) => ({
    color: COLORS.gray[600],
    fontSize: isRowVariant ? '10px' : '9px',
    fontWeight: 500
  }),
  drawerIcon: {
    fontSize: '12px',
    color: COLORS.gray[400]
  },
  drawerText: {
    color: COLORS.slate[500],
    fontSize: '10px',
    fontWeight: 600
  }
};

export const taskCardAvatarStyles = {
  width: 24,
  height: 24,
  fontSize: '11px',
  bgcolor: COLORS.primary,
  fontWeight: 600,
  border: `2px solid ${COLORS.white}`,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
};
